import { useEffect, useState } from "react";
import { money } from "../../utils/format";
import { clientsApi, vendorApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { LockedFeature } from "../../components/common/LockedFeature";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { Pager } from "../../components/common/Pager";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { VendorDetail } from "./VendorDetail";

/**
 * Vendors: the list, and the way in to one of them (#215).
 *
 * This page absorbed the old Clients tab. The user's complaint: "It feels
 * a little awkward to have a tab just to add new vendors called 'client'
 * and then a separate tab to assign them inventory." So adding a vendor
 * happens here, next to the list of them, and everything you then do with
 * one lives on its detail view.
 *
 * app.Clients is still the storage -- see V038. Only the navigation
 * changed; there was never a second table.
 */


export function VendorsPage() {
  const { notify } = useToast();
  /* VENDORS ARE NOT FREE (#445). PRICING_TIERS.md is unambiguous --
     "Harvester tier is for your own records. Business tier is for
     records someone else relies on -- a vendor, a customer, or a
     regulator", and the table gives Free a dash for "where jars went".
     HoneyChain is the flag whose own description says "...who got jars,
     paid/not-paid". */
  const { hasFeature, canChangeLists } = useFeatureTheme();
  const mayHaveVendors = hasFeature("HoneyChain");
  const [balances, setBalances] = useState(null);
  const [clients, setClients] = useState([]);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState(null);
  // Paging (#329). GET /clients returned 50 vendors and the page showed
  // them with nothing to say more existed.
  const [pageOffset, setPageOffset] = useState(0);
  const [total, setTotal] = useState(null);
  const [pageLimit, setPageLimit] = useState(null);
  const [paging, setPaging] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", contact_email: "", contact_phone: "" });

  async function load() {
    // The two calls are deliberately NOT a single Promise.all.
    //
    // Balances are HoneyBusiness (Business); the vendor LIST is
    // HoneyChain (Harvester). Under Promise.all a 403 on balances
    // rejected the whole thing, so a Harvester user lost the vendor list
    // they ARE entitled to and saw the "could not load" state instead --
    // data they own, hidden by a feature they do not have. Still true,
    // and still the reason these are sequential.
    //
    // THE TIERS NAMED HERE WERE WRONG UNTIL #445. This said the list was
    // "Free-tier business core" and that "#186 promises it at every
    // tier". #186 is one line -- "Pricing tiers and feature allocation
    // decided" -- and its actual output is docs/PRICING_TIERS.md, which
    // gives Free a dash for "where jars went" and says in its own words
    // that a vendor is what Business is FOR.
    //
    // That citation is why the endpoints went ungated for months: the
    // claim looked sourced. A comment naming a task number is not the
    // same as a comment that is right.
    try {
      const c = await clientsApi.list({ offset: pageOffset });
      setClients(c.clients || []);
      setTotal(c.total ?? null);
      setPageLimit(c.limit ?? null);
      setFailed(false);
    } catch {
      // Distinct from "no vendors yet": showing an empty list to someone
      // with twenty customers reads as lost data.
      setFailed(true);
      return;
    }

    // Best-effort. null means "no balances to show" -- either the tier does
    // not include them or the call failed -- and the columns below hide
    // themselves rather than rendering blanks that look like zero owed.
    try {
      const b = await vendorApi.balances();
      setBalances(b.balances || []);
    } catch {
      setBalances(null);
    }
  }
  // pageOffset in the deps, so paging reloads. load() reads it directly
  // rather than taking it as an argument because it also fetches
  // balances and would otherwise need two paths through the same guard.
  /* A locked account must not fetch. Both calls would 403 and the
     catch above would set `failed`, so a Free user would be told
     "Couldn't load your vendors" -- which reads as breakage rather than
     as a tier boundary, and sends them to support instead of to the
     upgrade they actually need. */
  useEffect(() => {
    if (mayHaveVendors) load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pageOffset, mayHaveVendors]);

  /* RETIRED VENDORS (#536). Retiring takes a vendor off the list above, and
     the only way back used to be to remember it existed. Loaded when the
     section is opened, not on every visit: most accounts never retire one.
     The detail page already restores, takes a final payment and accepts a
     return, so a row here simply opens it. */
  const [retiredOpen, setRetiredOpen] = useState(false);
  const [retired, setRetired] = useState(null);
  async function toggleRetired() {
    const next = !retiredOpen;
    setRetiredOpen(next);
    if (!next) return;
    try {
      const c = await clientsApi.list({ include_retired: "true" });
      setRetired((c.clients || []).filter((v) => v.is_active === false));
    } catch {
      setRetired([]);
    }
  }

  async function submitAdd() {
    if (!addForm.name.trim()) return notify("A vendor needs a name", "warning");
    try {
      await clientsApi.create({
        name: addForm.name.trim(),
        contact_email: addForm.contact_email || undefined,
        contact_phone: addForm.contact_phone || undefined,
      });
      notify("Vendor added", "success");
      setAddOpen(false);
      setAddForm({ name: "", contact_email: "", contact_phone: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not add the vendor", "danger");
    }
  }

  /* BEFORE the loading and failed branches, so a locked account sees the
     lock rather than a spinner that resolves into an error. */
  if (!mayHaveVendors) {
    return (
      <div>
        <h1>Vendors</h1>
        <LockedFeature
          enabled={false}
          featureLabel="Vendors"
          previewText={"Keep a list of the shops and people you sell to, and a simple log of "
            + "what each one took and whether they have paid."}
        />
      </div>
    );
  }

  if (selected) {
    return <VendorDetail clientId={selected} onBack={() => setSelected(null)} onChanged={load} />;
  }

  if (failed) {
    return (
      <div>
        <h1>Vendors</h1>
        <Card>
          <p>Couldn't load your vendors. Refresh to try again.</p>
          <Button onClick={load}>Try again</Button>
        </Card>
      </div>
    );
  }

  // Balances only cover vendors with activity. Everyone else still belongs
  // in the list -- a vendor you have not sent anything to yet is not
  // missing, just new.
  // null means the ledger is not available to this account (V048) or the
  // call failed; either way there is nothing truthful to show.
  // Two different absences, and conflating them was a live bug waiting to
  // happen. null means "no ledger for this account" (tier, or the call
  // failed). A ledger that IS present but whose rows carry no money keys
  // means #235 -- the owner has hidden money from this member. Either way
  // the columns go, but only checking `!== null` would have rendered
  // money(undefined) as a confident "$0.00 owed" for every vendor.
  const showBalances =
    balances !== null &&
    (balances.length === 0 || "outstanding" in balances[0]);
  // ...and the two absences need different words. Telling a member on a
  // Business account to upgrade to Business is nonsense, and would send
  // them to the owner with the wrong question.
  const moneyHiddenFromMe =
    balances !== null && balances.length > 0 && !("outstanding" in balances[0]);
  const byId = new Map((balances || []).map((b) => [b.client_id, b]));
  /* The row builder copied only id and name, so every other field the
     clients endpoint returns -- loyalty_tier, contact_email,
     contact_phone -- was fetched and dropped one line later. A cut-wire
     in miniature: the API answers the question and the client throws the
     answer away. loyalty_tier is carried now because a column reads it. */
  const rows = clients.map((c) => ({
    client_id: c.client_id,
    client_name: c.name,
    loyalty_tier: c.loyalty_tier,
    ...(byId.get(c.client_id) || { assigned_value: 0, returned_value: 0, paid: 0, outstanding: 0 }),
  }));
  const owed = rows.reduce((sum, r) => sum + Number(r.outstanding || 0), 0);

  return (
    <div>
      <h1>Vendors</h1>

      {/* Money only when there are balances to show. Rendering a big
          confident "$0.00 owed" from an absent ledger would be worse than
          showing nothing -- it is a number a beekeeper might act on. */}
      {showBalances ? (
        <Card
          title="Outstanding"
          actions={canChangeLists === false ? null : <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>Add Vendor</Button>}
        >
          <div style={{ fontSize: "var(--font-size-xxl, 2.5rem)", fontWeight: 700 }}>{money(owed)}</div>
          <p style={{ color: "var(--color-text-muted)" }}>Total owed across all vendors.</p>
        </Card>
      ) : (
        <Card actions={canChangeLists === false ? null : <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>Add Vendor</Button>}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            {moneyHiddenFromMe
              ? "This account's owner keeps the money side private. Everything else here works as normal."
              : "Keep track of who owes you, what they still hold and when they last paid, so your numbers reconcile and nobody has to take your word for it. Available on the Business plan."}
          </p>
        </Card>
      )}

      {/* Reported 28 Aug: these were touching. */}
      <div style={{ height: "var(--space-5)" }} />

      <Card title="Your vendors" className="section-spacing">
        <DataTable
          rows={rows}
          rowKey="client_id"
          columns={[
            { key: "client_name", header: "Vendor" },
            /* RESTORED FROM THE DELETED ClientsPage (#418). It was the one
               column on that page with no equivalent here, and it is real
               user-entered data -- there is nowhere else in the app to
               read it back.

               Its neighbour on that page, "Total Purchased", was NOT
               restored and should not be: app.Clients.total_purchased is
               incremented only by create_client_transaction, whose sole
               caller is the unreachable ClientDetailPage (#431), so it
               reads 0 for anybody who sells honey the discoverable way.
               "Sent" below is the same quantity computed correctly from
               the honey ledger -- a lifetime SUM over HoneyAssignments --
               so restoring the broken one would have put a permanent zero
               beside a true figure. */
            {
              key: "loyalty_tier",
              header: "Tier",
              render: (r) => r.loyalty_tier
                || <span style={{ color: "var(--color-text-muted)" }}>—</span>,
            },
            // The money columns come and go with the ledger, rather than
            // showing zeros that read as "nothing owed" (V048).
            ...(showBalances
              ? [
                  { key: "assigned_value", header: "Sent", render: (r) => money(r.assigned_value) },
                  { key: "returned_value", header: "Returned", render: (r) => money(r.returned_value) },
                  { key: "paid", header: "Paid", render: (r) => money(r.paid) },
                  {
                    key: "outstanding",
                    header: "Outstanding",
                    render: (r) => {
                      const v = Number(r.outstanding);
                      if (v < 0) return <Badge tone="info">{money(-v)} in credit</Badge>;
                      if (v === 0) return <Badge tone="success">Settled</Badge>;
                      return <Badge tone="warning">{money(v)}</Badge>;
                    },
                  },
                ]
              : []),
          ]}
          onRowClick={(r) => setSelected(r.client_id)}
          emptyTitle="No vendors yet"
          emptyDescription="Add the shops and people you sell honey to."
          emptyAction={canChangeLists === false ? null : <Button onClick={() => setAddOpen(true)}>Add Vendor</Button>}
        />
        <Pager
          total={total}
          limit={pageLimit}
          offset={pageOffset}
          onChange={(next) => { setPageOffset(next); }}
          busy={paging}
          unit="vendors"
        />
      </Card>

      <Card className="section-spacing">
        <Button variant="ghost" size="sm" aria-expanded={retiredOpen} onClick={toggleRetired}>
          {retiredOpen ? "Hide retired vendors" : "Retired vendors"}
        </Button>
        {retiredOpen ? (
          retired === null ? <p>Loading…</p> : retired.length === 0 ? (
            <p style={{ color: "var(--color-text-muted)" }}>No retired vendors.</p>
          ) : (
            <>
              <p style={{ color: "var(--color-text-muted)" }}>
                Open one to restore it, record a final payment or take back what it still holds.
              </p>
              <DataTable
                rows={retired.map((v) => ({
                  client_id: v.client_id,
                  client_name: v.name,
                  ...(byId.get(v.client_id) || {}),
                }))}
                rowKey="client_id"
                columns={[
                  { key: "client_name", header: "Vendor" },
                  ...(showBalances ? [{
                    key: "outstanding",
                    header: "Outstanding",
                    render: (r) => {
                      const v = Number(r.outstanding || 0);
                      if (v > 0) return <Badge tone="warning">{money(v)}</Badge>;
                      if (v < 0) return <Badge tone="info">{money(-v)} in credit</Badge>;
                      return <Badge tone="success">Settled</Badge>;
                    },
                  }] : []),
                ]}
                onRowClick={(r) => setSelected(r.client_id)}
              />
            </>
          )
        ) : null}
      </Card>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add a Vendor" footer={<Button onClick={submitAdd}>Add Vendor</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="Name" required>
            {(id, aria) => <input id={id} {...aria} value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Contact email (optional)">
            {(id, aria) => <input id={id} {...aria} type="email" value={addForm.contact_email}
              onChange={(e) => setAddForm((f) => ({ ...f, contact_email: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Contact phone (optional)">
            {(id, aria) => <input id={id} {...aria} value={addForm.contact_phone}
              onChange={(e) => setAddForm((f) => ({ ...f, contact_phone: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>
    </div>
  );
}
